import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "../api/baseurl";
import "./a4shett.css";
import tmplLogo from "../images/logoForLightTheme.png";

const PaymentReceipt = () => {
  const location = useLocation();
  const userid = location.state?.userid;
  const paymentId = location.state?.paymentId;
  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("data")).token;

  const [loading, setLoading] = useState(true);
  const [data, setdata] = useState(null); // User details
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [appointmentData, setAppointmentData] = useState(null);
  const [packages, setPackages] = useState(null);
  const [testDetails, setTestDetails] = useState([]);
  const [recommendedTestDetails, setRecommendedTestDetails] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch payment and user details in parallel
        const [paymentResponse, userResponse] = await Promise.all([
          axios.get(`/api/user/userpayment/getById/${paymentId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`/api/employeedetails/userid=${userid}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        ]);

        const payDetails = paymentResponse.data;
        const usrDetails = userResponse.data;
        setPaymentDetails(payDetails);
        setdata(usrDetails);

        // Fetch appointment details using paymentId
        try {
          const appointmentResponse = await axios.get(`/api/employeeappointment/paymentid=${paymentId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const apptData = appointmentResponse.data;
          setAppointmentData(apptData);

          if (apptData) {
            // Fetch package via /api/package/:id (same as paymentReference.js) to get price
            if (apptData.packageid) {
              try {
                const pkgRes = await axios.get(`/api/package/${apptData.packageid}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                setPackages(pkgRes.data);
              } catch (err) {
                // fallback: use embedded pack object if present
                if (apptData.pack) setPackages(apptData.pack);
                console.error("Error fetching package details:", err);
              }
            } else if (apptData.pack) {
              setPackages(apptData.pack);
            }

            // Extract test IDs by source, then fetch full test details (including testCost)
            // from /api/labtest/:id — same approach as paymentReference.js
            if (apptData.additionalTest && apptData.additionalTest.length > 0) {
              const manualTestIds = apptData.additionalTest
                .filter(t => t.testSource === "Manual")
                .map(t => t.testid || t.testId)
                .filter(Boolean);

              const recTestIds = apptData.additionalTest
                .filter(t => t.testSource === "Recommended")
                .map(t => t.testid || t.testId)
                .filter(Boolean);

              if (manualTestIds.length > 0) {
                const manualReqs = manualTestIds.map(id =>
                  axios.get(`/api/labtest/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  })
                );
                const manualResponses = await Promise.all(manualReqs);
                setTestDetails(manualResponses.map(r => r.data));
              }

              if (recTestIds.length > 0) {
                const recReqs = recTestIds.map(id =>
                  axios.get(`/api/labtest/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  })
                );
                const recResponses = await Promise.all(recReqs);
                setRecommendedTestDetails(recResponses.map(r => r.data));
              }
            }
          }
        } catch (apptError) {
          console.error("Error fetching appointment details:", apptError);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch payment receipt details");
        setLoading(false);
      }
    };

    if (userid && paymentId) {
      fetchData();
    } else {
      toast.error("Invalid user or payment ID");
      setLoading(false);
    }
  }, [userid, paymentId, token]);

  const totalTransactionCost = paymentDetails ? parseFloat(paymentDetails.paymentAmount) : 0;
  const totalPlatformFee = paymentDetails ? parseFloat(paymentDetails.platformFee) : 0;
  const actualPrice = totalTransactionCost - totalPlatformFee;

  const totalAdditionalTestCost = testDetails.reduce(
    (sum, test) => sum + (test?.testCost?.cost || 0),
    0
  );

  const totalRecommendedTestCost = recommendedTestDetails.reduce(
    (sum, test) => sum + (test?.testCost?.cost || 0),
    0
  );

  const totalTestCost = totalAdditionalTestCost + totalRecommendedTestCost;
  const packagePrice = packages?.price || 0;

  // If we have package or test details, use their computed values; otherwise fallback to actualPrice
  const hasDetails = packages || testDetails.length > 0 || recommendedTestDetails.length > 0;
  const subtotalPrice = hasDetails ? parseFloat(packagePrice + totalTestCost) : actualPrice;

  const cgst = 0;
  const sgst = 0;
  const invoice = paymentDetails?.razorpay_payment_id || "";
  const packageId = appointmentData?.packageid || packages?.id || null;

  const Printbill = (e) => {
    e.preventDefault();
    window.print();
  };

  const handleClose = (e) => {
    e.preventDefault();
    navigate(-1);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="loading"></div>
      </div>
    );
  }

  if (!data || !paymentDetails) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
        <h4>Receipt Data Not Available</h4>
        <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  return (
    <div>
      <ToastContainer />
      <style>{`
        .receipt-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 40px;
        }
        
        .receipt-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 30px;
        }
        
        .company-info h6 {
          margin: 5px 0;
        }
        
        .logo-section {
          text-align: right;
        }
        
        .bill-reference-section {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
        }
        
        .bill-to h6 {
          margin: 5px 0;
        }
        
        .reference-number {
          text-align: right;
        }
        
        .reference-number h6 {
          margin: 5px 0;
        }
        
        .total-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 20px 0;
        }
        
        .breakdown-section {
          display: flex;
          justify-content: flex-end;
          margin: 30px 0;
        }
        
        .breakdown-details {
          text-align: right;
          min-width: 250px;
        }
        
        .breakdown-details h6 {
          margin: 8px 0;
        }
        
        .breakdown-details h2 {
          margin-top: 15px;
          padding-top: 15px;
          border-top: 2px solid #333;
        }
        
        .action-buttons {
          display: flex;
          justify-content: flex-end;
          gap: 15px;
          margin-top: 40px;
        }
        
        .divider {
          height: 2px;
          background-color: #333;
          margin: 20px 0;
        }
        
        @media print {
          .action-buttons {
            display: none;
          }
        }
      `}</style>

      <div id="layoutDrawer_content">
        <main>
          <div className="a4-sheet">
            <header className="bg-primary">
              <div className="container-xl px-5">
                <h1 className="text-white py-3 mb-0 display-6"></h1>
              </div>
            </header>

            <div className="receipt-container">
              <form>
                {/* Header Section */}
                <div className="receipt-header">
                  <div className="company-info">
                    <h5>
                      <b>RECEIPT</b>
                    </h5>
                    <br />
                    <h6>
                      <b>Timespan pvt ltd.</b>
                    </h6>
                    <h6>Bangalore, Karnataka</h6>
                    <h6>PIN</h6>
                    <h6>GSTIN- 29AAJCC7045D1ZM</h6>
                  </div>
                  <div className="logo-section">
                    <img src={tmplLogo} style={{ maxHeight: "70px", maxWidth: "160px", objectFit: "contain" }} alt="Company Logo" />
                  </div>
                </div>

                {/* Bill To and Reference Section */}
                <div className="bill-reference-section">
                  <div className="bill-to">
                    <h6>
                      <b>Bill To</b>
                    </h6>
                    <h6>{data?.email}</h6>
                    <h6>{data?.address}</h6>
                    <h6>PAN-{data?.pan}</h6>
                  </div>
                  <div className="reference-number">
                    <h6>
                      <b>Reference Number:</b>
                    </h6>
                    <h6>{invoice}</h6>
                  </div>
                </div>

                <div className="divider"></div>

                {/* Total Amount Header */}
                <div className="total-header">
                  <h2>Total Amount</h2>
                  <h2>{totalTransactionCost.toFixed(2)} ₹</h2>
                </div>

                <div className="divider"></div>

                {/* Breakdown Section */}
                <div className="breakdown-section">
                  <div className="breakdown-details">
                    {packageId ? (
                      <>
                        <h6>Package Price: {packagePrice.toFixed(2)}</h6>
                        {testDetails.length > 0 && (
                          <h6>Additional Test Cost: {totalAdditionalTestCost.toFixed(2)}</h6>
                        )}
                        {recommendedTestDetails.length > 0 && (
                          <h6>Recommended Tests Cost: {totalRecommendedTestCost.toFixed(2)}</h6>
                        )}
                        {(testDetails.length > 0 || recommendedTestDetails.length > 0) && (
                          <h6>Subtotal: {subtotalPrice.toFixed(2)}</h6>
                        )}
                      </>
                    ) : (
                      <>
                        {testDetails.length > 0 && (
                          <h6>Additional Test Cost: {totalAdditionalTestCost.toFixed(2)}</h6>
                        )}
                        {recommendedTestDetails.length > 0 && (
                          <h6>Recommended Tests Cost: {totalRecommendedTestCost.toFixed(2)}</h6>
                        )}
                        <h6>Amount: {subtotalPrice.toFixed(2)}</h6>
                      </>
                    )}
                    <h6>CGST: {cgst.toFixed(2)}</h6>
                    <h6>SGST: {sgst.toFixed(2)}</h6>
                    <h6>Platform Fee: {totalPlatformFee.toFixed(2)}</h6>
                    <h2>Total Amount: {totalTransactionCost.toFixed(2)} ₹</h2>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                  <button
                    onClick={(e) => Printbill(e)}
                    className="btn btn-primary"
                    type="button"
                  >
                    Print
                  </button>
                  <button
                    onClick={(e) => handleClose(e)}
                    className="btn btn-primary"
                    type="button"
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PaymentReceipt;